import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Building, Search, Plus, Eye, FileText, AlertTriangle, CheckCircle,
  Clock, Users, TrendingUp, ChevronRight, Calendar, BarChart2,
  Shield, Globe, Cloud, Lock, ClipboardCheck, Filter, Download,
  Trash2, Edit2, Play, ChevronDown, ChevronUp, X, Save, Loader2,
  ArrowLeft, Cpu, AlertCircle, Info, FileDown, TicketCheck, Copy, Check, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  getVendorDashboard, getVendors, getAssessments, getQuestionnaires,
  getQuestionnaire, getQuestionnaireCategories, createVendor, deleteVendor,
  createAssessment, submitAssessmentResponses, completeAssessment, getAssessment,
  createCustomQuestionnaire, deleteCustomQuestionnaire,
  getRiskLevelColor, getRiskLevelLabel, getStatusColor, getStatusLabel,
  getTierColor, getTierLabel, formatDate, isOverdue
} from '../../services/vendorAssessment';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  security: Shield, privacy: Lock, risk: AlertTriangle, compliance: ClipboardCheck,
  vendor: Building, cloud: Cloud, saas: Globe, ai: Cpu
};

const riskColors = { high: 'bg-red-100 text-red-700 border-red-200', medium: 'bg-amber-100 text-amber-700 border-amber-200', low: 'bg-emerald-100 text-emerald-700 border-emerald-200', not_assessed: 'bg-gray-100 text-gray-600 border-gray-200' };
const statusColors = { not_started: 'bg-gray-100 text-gray-600', in_progress: 'bg-blue-100 text-blue-700', pending_review: 'bg-amber-100 text-amber-700', completed: 'bg-emerald-100 text-emerald-700' };

export default function VendorAssessmentPage() {
  const navigate = useNavigate();
  const { vendorId, assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Determine active tab from URL path or query params
  const getActiveTab = () => {
    const path = location.pathname;
    // Check path-based routing first (from sidebar links)
    if (path.includes('/vendor-assessment/vendors')) return 'vendors';
    if (path.includes('/vendor-assessment/assessments')) return 'assessments';
    if (path.includes('/vendor-assessment/questionnaires')) return 'questionnaires';
    // Fall back to query param for backward compatibility
    return searchParams.get('tab') || 'dashboard';
  };
  
  const tab = getActiveTab();

  // If we have a specific assessment to fill, show that
  if (assessmentId) return <AssessmentFillView assessmentId={assessmentId} navigate={navigate} />;
  if (vendorId) return <VendorDetailView vendorId={vendorId} navigate={navigate} />;

  return <VendorDashboardTabs activeTab={tab} navigate={navigate} />;
}

// ==================== MAIN TABS ====================
function VendorDashboardTabs({ activeTab, navigate }) {
  const [tab, setTab] = useState(activeTab);
  const [dashboard, setDashboard] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showNewAssessment, setShowNewAssessment] = useState(false);
  const [showCreateQuestionnaire, setShowCreateQuestionnaire] = useState(false);
  const [previewQuestionnaire, setPreviewQuestionnaire] = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);

  // Sync tab with activeTab prop when URL changes
  useEffect(() => {
    setTab(activeTab);
  }, [activeTab]);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (tab === 'questionnaires') loadQuestionnaires();
    if (tab === 'assessments') loadAssessments();
    if (tab === 'vendors') loadVendors();
  }, [tab, catFilter]);

  // Handle tab change with URL update
  const handleTabChange = (newTab) => {
    setTab(newTab);
    // Update URL to reflect tab change
    if (newTab === 'dashboard') {
      navigate('/grc/vendor-assessment');
    } else if (newTab === 'vendors') {
      navigate('/grc/vendor-assessment/vendors');
    } else if (newTab === 'assessments') {
      navigate('/grc/vendor-assessment/assessments');
    } else if (newTab === 'questionnaires') {
      navigate('/grc/vendor-assessment/questionnaires');
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([getVendorDashboard(), getQuestionnaireCategories()]);
      setDashboard(d);
      setCategories(c);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadVendors = async () => {
    try { setVendors(await getVendors({ search })); } catch (e) { console.error(e); }
  };
  const loadAssessments = async () => {
    try { setAssessments(await getAssessments({})); } catch (e) { console.error(e); }
  };
  const loadQuestionnaires = async () => {
    try { setQuestionnaires(await getQuestionnaires(catFilter ? { category: catFilter } : {})); } catch (e) { console.error(e); }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor and all assessments?')) return;
    try { await deleteVendor(id); toast.success('Vendor deleted'); loadVendors(); loadAll(); } catch { toast.error('Failed'); }
  };

  const handleDeleteQuestionnaire = async (id) => {
    if (!window.confirm('Delete this custom questionnaire?')) return;
    try { await deleteCustomQuestionnaire(id); toast.success('Deleted'); loadQuestionnaires(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="vendor-assessment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Risk Management</h1>
          <p className="text-sm text-gray-500 mt-1">Assess and monitor third-party vendor risks</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments">Assessments</TabsTrigger>
          <TabsTrigger value="questionnaires" data-testid="tab-questionnaires">Questionnaires</TabsTrigger>
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard">
          {dashboard && <DashboardView dashboard={dashboard} navigate={navigate} />}
        </TabsContent>

        {/* VENDORS */}
        <TabsContent value="vendors">
          <div className="flex items-center justify-between mb-4">
            <div className="relative w-80">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <Input placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadVendors()} className="pl-9" data-testid="vendor-search" />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowCatalog(true)} data-testid="browse-catalog-btn">
                <Database className="w-4 h-4 mr-2" />
                Browse Vendor Catalog
              </Button>
              <Button onClick={() => setShowAddVendor(true)} data-testid="add-vendor-btn"><Plus className="w-4 h-4 mr-2" />Add Vendor</Button>
            </div>
          </div>
          <VendorList vendors={vendors} navigate={navigate} onDelete={handleDeleteVendor} />
        </TabsContent>

        {/* ASSESSMENTS */}
        <TabsContent value="assessments">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">All Assessments</h2>
            <Button onClick={() => setShowNewAssessment(true)} data-testid="new-assessment-btn"><Plus className="w-4 h-4 mr-2" />New Assessment</Button>
          </div>
          <AssessmentList assessments={assessments} navigate={navigate} />
        </TabsContent>

        {/* QUESTIONNAIRES */}
        <TabsContent value="questionnaires">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Questionnaire Library</h2>
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger className="w-48" data-testid="cat-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setShowCreateQuestionnaire(true)} data-testid="create-questionnaire-btn"><Plus className="w-4 h-4 mr-2" />Create Custom</Button>
          </div>
          <QuestionnaireGrid questionnaires={questionnaires} onPreview={setPreviewQuestionnaire} onDelete={handleDeleteQuestionnaire} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showAddVendor && <AddVendorDialog open={showAddVendor} onClose={() => setShowAddVendor(false)} onSuccess={() => { setShowAddVendor(false); loadVendors(); loadAll(); }} />}
      {showNewAssessment && <NewAssessmentDialog open={showNewAssessment} onClose={() => setShowNewAssessment(false)} onSuccess={(id) => { setShowNewAssessment(false); navigate(`/grc/vendor-assessment/assessments/${id}`); }} />}
      {showCreateQuestionnaire && <CreateQuestionnaireDialog open={showCreateQuestionnaire} onClose={() => setShowCreateQuestionnaire(false)} onSuccess={() => { setShowCreateQuestionnaire(false); loadQuestionnaires(); }} />}
      {previewQuestionnaire && <QuestionnairePreviewDialog questionnaire={previewQuestionnaire} onClose={() => setPreviewQuestionnaire(null)} />}
      {showCatalog && <VendorCatalogModal open={showCatalog} onClose={() => setShowCatalog(false)} onSuccess={() => { setShowCatalog(false); loadVendors(); loadAll(); }} />}
    </div>
  );
}

// ==================== DASHBOARD VIEW ====================
function DashboardView({ dashboard, navigate }) {
  const stats = [
    { label: 'Total Vendors', value: dashboard.total_vendors, icon: Building, color: 'text-blue-600 bg-blue-50' },
    { label: 'Assessments', value: dashboard.total_assessments, icon: FileText, color: 'text-indigo-600 bg-indigo-50' },
    { label: 'In Progress', value: dashboard.assessments_in_progress, icon: Clock, color: 'text-amber-600 bg-amber-50' },
    { label: 'Completed', value: dashboard.assessments_completed, icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Overdue', value: dashboard.assessments_overdue, icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-5 gap-4">
        {stats.map(s => (
          <Card key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.color}`}><s.icon className="w-5 h-5" /></div>
                <div>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Risk Distribution */}
      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Vendors by Risk Level</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(dashboard.vendors_by_risk_level || {}).length === 0 ? (
              <p className="text-sm text-gray-400">No risk data yet</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(dashboard.vendors_by_risk_level).map(([level, count]) => (
                  <div key={level} className="flex items-center justify-between">
                    <Badge className={riskColors[level]}>{getRiskLevelLabel(level)}</Badge>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Recent Assessments</CardTitle></CardHeader>
          <CardContent>
            {(dashboard.recent_assessments || []).length === 0 ? (
              <p className="text-sm text-gray-400">No assessments yet</p>
            ) : (
              <div className="space-y-2">
                {dashboard.recent_assessments.slice(0, 5).map(a => (
                  <div key={a.id} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 px-2 rounded" onClick={() => navigate(`/grc/vendor-assessment/assessments/${a.id}`)}>
                    <div>
                      <p className="text-sm font-medium">{a.vendor_name}</p>
                      <p className="text-xs text-gray-400">{a.questionnaire_name}</p>
                    </div>
                    <Badge className={statusColors[a.status]}>{getStatusLabel(a.status)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ==================== VENDOR LIST ====================
function VendorList({ vendors, navigate, onDelete }) {
  if (vendors.length === 0) return (
    <Card><CardContent className="py-12 text-center"><Building className="w-12 h-12 mx-auto text-gray-300 mb-3" /><p className="text-gray-500">No vendors yet. Add your first vendor to get started.</p></CardContent></Card>
  );

  return (
    <div className="space-y-3">
      {vendors.map(v => (
        <Card key={v.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/grc/vendor-assessment/vendors/${v.id}`)} data-testid={`vendor-${v.id}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Building className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="font-semibold">{v.name}</p>
                <p className="text-sm text-gray-500">{v.industry || 'N/A'} &middot; {v.assessment_count || 0} assessments</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={riskColors[v.overall_risk_level]}>{getRiskLevelLabel(v.overall_risk_level)}</Badge>
              {v.overall_risk_score != null && <span className="text-sm font-mono font-semibold">{Math.round(v.overall_risk_score)}%</span>}
              <Button variant="ghost" size="icon" onClick={e => { e.stopPropagation(); onDelete(v.id); }} data-testid={`delete-vendor-${v.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ==================== ASSESSMENT LIST ====================
function AssessmentList({ assessments, navigate }) {
  if (assessments.length === 0) return (
    <Card><CardContent className="py-12 text-center"><FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" /><p className="text-gray-500">No assessments yet.</p></CardContent></Card>
  );

  return (
    <div className="space-y-3">
      {assessments.map(a => (
        <Card key={a.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/grc/vendor-assessment/assessments/${a.id}`)} data-testid={`assessment-${a.id}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold">{a.title}</p>
              <p className="text-sm text-gray-500">{a.vendor_name} &middot; {a.questionnaire_name}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32 bg-gray-100 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${a.completion_percentage || 0}%` }} />
              </div>
              <span className="text-xs text-gray-500 w-10">{Math.round(a.completion_percentage || 0)}%</span>
              <Badge className={statusColors[a.status]}>{getStatusLabel(a.status)}</Badge>
              {a.risk_score != null && <Badge className={riskColors[a.risk_level]}>{Math.round(a.risk_score)}%</Badge>}
              {a.due_date && isOverdue(a.due_date) && a.status !== 'completed' && <Badge className="bg-red-50 text-red-600">Overdue</Badge>}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ==================== QUESTIONNAIRE GRID ====================
function QuestionnaireGrid({ questionnaires, onPreview, onDelete }) {
  const [expandedId, setExpandedId] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);

  const handlePreview = async (q) => {
    setLoadingPreview(q.id);
    try {
      const full = await getQuestionnaire(q.id);
      onPreview(full);
    } catch { toast.error('Failed to load questionnaire'); }
    setLoadingPreview(null);
  };

  const handleDownload = async (q) => {
    try {
      const full = await getQuestionnaire(q.id);
      const text = generateQuestionnaireText(full);
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${full.name.replace(/\s+/g, '_')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch { toast.error('Failed'); }
  };

  if (questionnaires.length === 0) return <Card><CardContent className="py-12 text-center text-gray-400">No questionnaires found</CardContent></Card>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {questionnaires.map(q => {
        const CatIcon = categoryIcons[q.category] || FileText;
        return (
          <Card key={q.id} className="hover:shadow-md transition-shadow" data-testid={`questionnaire-${q.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-50"><CatIcon className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="font-semibold text-sm">{q.name}</p>
                    <p className="text-xs text-gray-400">v{q.version} &middot; {q.total_questions} questions &middot; {q.estimated_time}</p>
                  </div>
                </div>
                {q.id?.startsWith('custom_') && (
                  <Button variant="ghost" size="icon" onClick={() => onDelete(q.id)} data-testid={`delete-q-${q.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-3 line-clamp-2">{q.description}</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {(q.frameworks || []).map(f => <Badge key={f} variant="outline" className="text-xs">{f}</Badge>)}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handlePreview(q)} disabled={loadingPreview === q.id} data-testid={`preview-q-${q.id}`}>
                  {loadingPreview === q.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Eye className="w-3 h-3 mr-1" />}Preview
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDownload(q)} data-testid={`download-q-${q.id}`}>
                  <Download className="w-3 h-3 mr-1" />Download
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function generateQuestionnaireText(q) {
  let text = `${q.name}\nVersion: ${q.version}\n${q.description}\nFrameworks: ${(q.frameworks || []).join(', ')}\nTotal Questions: ${q.total_questions}\n\n`;
  (q.sections || []).forEach((s, si) => {
    text += `${'='.repeat(60)}\nSection ${si + 1}: ${s.name}\n${s.description || ''}\n${'='.repeat(60)}\n\n`;
    (s.questions || []).forEach((q, qi) => {
      text += `  ${qi + 1}. ${q.text}\n     Type: ${q.type}${q.required ? ' (Required)' : ''} | Weight: ${q.weight || 'none'}\n`;
      if (q.options) q.options.forEach(o => { text += `       [ ] ${o.label}${o.score != null ? ` (Score: ${o.score})` : ''}\n`; });
      if (q.help_text) text += `     Note: ${q.help_text}\n`;
      text += '\n';
    });
  });
  return text;
}

// ==================== QUESTIONNAIRE PREVIEW DIALOG ====================
function QuestionnairePreviewDialog({ questionnaire, onClose }) {
  const q = questionnaire;
  const [expandedSection, setExpandedSection] = useState(q.sections?.[0]?.id || '');

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">{q.name}</DialogTitle>
          <DialogDescription>{q.description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 mb-4">
          {(q.frameworks || []).map(f => <Badge key={f} variant="outline">{f}</Badge>)}
          <Badge>{q.total_questions} questions</Badge>
          <Badge variant="outline">{q.estimated_time}</Badge>
          {q.uses_scoring && <Badge className="bg-blue-50 text-blue-700">Scored</Badge>}
        </div>
        <div className="space-y-3">
          {(q.sections || []).map((section, si) => (
            <div key={section.id} className="border rounded-lg">
              <button
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedSection(expandedSection === section.id ? '' : section.id)}
              >
                <div className="text-left">
                  <p className="font-semibold text-sm">Section {si + 1}: {section.name}</p>
                  <p className="text-xs text-gray-500">{section.description} &middot; {(section.questions || []).length} questions</p>
                </div>
                {expandedSection === section.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedSection === section.id && (
                <div className="border-t p-4 space-y-4">
                  {(section.questions || []).map((question, qi) => (
                    <div key={question.id} className="pl-4 border-l-2 border-blue-200">
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-mono text-gray-400 mt-0.5">Q{qi + 1}</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{question.text}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{question.type}</Badge>
                            {question.required && <Badge className="text-xs bg-red-50 text-red-600">Required</Badge>}
                            {question.weight && question.weight !== 'none' && <Badge className={`text-xs ${question.weight === 'high' ? 'bg-red-50 text-red-600' : question.weight === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'}`}>{question.weight} weight</Badge>}
                          </div>
                          {question.options && (
                            <div className="mt-2 space-y-1">
                              {question.options.map((o, oi) => (
                                <div key={oi} className="flex items-center gap-2 text-xs text-gray-600">
                                  <div className="w-3 h-3 border rounded-full border-gray-300" />
                                  <span>{o.label}</span>
                                  {o.score != null && <span className="text-gray-400">(Score: {o.score})</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {question.help_text && <p className="text-xs text-blue-500 mt-1 flex items-center gap-1"><Info className="w-3 h-3" />{question.help_text}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== VENDOR CATALOG MODAL ====================
function VendorCatalogModal({ open, onClose, onSuccess }) {
  const [catalog, setCatalog] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState(null);

  useEffect(() => {
    if (open) loadCatalog();
  }, [open]);

  useEffect(() => {
    let results = catalog;
    if (category !== 'all') results = results.filter(v => v.category === category);
    if (search) {
      const s = search.toLowerCase();
      results = results.filter(v =>
        v.name.toLowerCase().includes(s) ||
        v.industry.toLowerCase().includes(s) ||
        v.country.toLowerCase().includes(s) ||
        v.description.toLowerCase().includes(s) ||
        (v.certifications || []).some(c => c.toLowerCase().includes(s)) ||
        (v.tags || []).some(t => t.includes(s))
      );
    }
    setFiltered(results);
  }, [catalog, search, category]);

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/vendor-assessment/vendors/catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatalog(res.data.vendors);
    } catch (e) {
      toast.error('Failed to load vendor catalog');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const selectAll = () => {
    const available = filtered.filter(v => !v.already_added).map(v => v.name);
    setSelected(new Set(available));
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/vendor-assessment/vendors/add-from-catalog`,
        { vendor_names: Array.from(selected) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.created} vendor(s) added successfully`);
      onSuccess();
    } catch (e) {
      toast.error('Failed to add vendors');
    } finally {
      setAdding(false);
    }
  };

  const catLabels = { csp: 'Cloud Providers', sub_processor: 'Sub-Processors / SaaS', security: 'Security & Identity', it_provider: 'IT Providers / DevOps' };
  const riskColors = { low: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700' };

  const getRiskScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col p-0" data-testid="vendor-catalog-modal">
        <div className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="text-lg font-semibold">Verified Vendor Catalog</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">Browse and add pre-verified vendors with risk scores, certifications, and compliance data</p>

          <div className="flex items-center gap-3 mt-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search vendors, certifications, industries..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="catalog-search"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[200px]" data-testid="catalog-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="csp">Cloud Providers</SelectItem>
                <SelectItem value="sub_processor">Sub-Processors / SaaS</SelectItem>
                <SelectItem value="security">Security & Identity</SelectItem>
                <SelectItem value="it_provider">IT Providers / DevOps</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No vendors match your search</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(v => {
                const isExpanded = expandedVendor === v.name;
                return (
                  <div key={v.name} className={`border rounded-lg transition-all ${v.already_added ? 'opacity-50 bg-gray-50' : selected.has(v.name) ? 'border-blue-400 bg-blue-50/30' : 'hover:border-gray-300'}`}>
                    <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => !v.already_added && toggleSelect(v.name)}>
                      <input
                        type="checkbox"
                        checked={v.already_added || selected.has(v.name)}
                        disabled={v.already_added}
                        onChange={() => {}}
                        className="w-4 h-4 rounded border-gray-300"
                        data-testid={`catalog-vendor-${v.name.replace(/\s+/g, '-').toLowerCase()}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{v.name}</span>
                          {v.already_added && <Badge variant="secondary" className="text-xs">Already Added</Badge>}
                          <Badge className={`text-xs ${riskColors[v.risk_tier] || 'bg-gray-100 text-gray-600'}`}>{v.risk_tier} risk</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{v.industry} &middot; {v.country}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <span className={`text-lg font-bold ${getRiskScoreColor(v.overall_risk_score)}`}>{Math.round(v.overall_risk_score)}%</span>
                          <p className="text-[10px] text-muted-foreground">Risk Score</p>
                        </div>
                        <div className="flex gap-1 max-w-[200px] flex-wrap">
                          {(v.certifications || []).slice(0, 3).map(c => (
                            <Badge key={c} variant="outline" className="text-[10px] px-1.5 py-0 whitespace-nowrap">{c}</Badge>
                          ))}
                          {(v.certifications || []).length > 3 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">+{v.certifications.length - 3}</Badge>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); setExpandedVendor(isExpanded ? null : v.name); }}>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-3 pt-0 border-t bg-muted/20">
                        <p className="text-sm text-muted-foreground mt-2">{v.description}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Certifications</p>
                            <div className="flex flex-wrap gap-1">
                              {(v.certifications || []).map(c => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Data Locations</p>
                            <div className="flex flex-wrap gap-1">
                              {(v.data_processing_locations || []).map(l => <Badge key={l} className="text-[10px] bg-blue-50 text-blue-700">{l}</Badge>)}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Sub-Processors</p>
                            <p className="text-sm font-semibold">{v.sub_processor_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Last Audit</p>
                            <p className="text-sm font-semibold">{v.last_audit_date || 'N/A'}</p>
                          </div>
                        </div>
                        {v.breach_history && (
                          <div className="mt-2">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Breach History</p>
                            <p className="text-xs text-muted-foreground">{v.breach_history}</p>
                          </div>
                        )}
                        {v.website && (
                          <a href={v.website} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline mt-2 inline-block">{v.website}</a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{filtered.length} vendors</span>
            <span>{selected.size} selected</span>
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={selectAll}>Select all available</Button>
            {selected.size > 0 && <Button variant="link" size="sm" className="h-auto p-0 text-xs text-red-500" onClick={() => setSelected(new Set())}>Clear</Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleAdd} disabled={selected.size === 0 || adding} data-testid="add-selected-vendors-btn">
              {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add {selected.size} Vendor{selected.size !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== ADD VENDOR DIALOG ====================
function AddVendorDialog({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', industry: '', description: '', risk_tier: 'medium', contact_name: '', contact_email: '', website: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Vendor name required'); return; }
    setSaving(true);
    try { await createVendor(form); toast.success('Vendor created'); onSuccess(); } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Vendor</DialogTitle><DialogDescription>Register a new third-party vendor</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Vendor Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Industry</Label><Input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} /></div>
            <div><Label>Risk Tier</Label>
              <Select value={form.risk_tier} onValueChange={v => setForm({ ...form, risk_tier: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High (Critical)</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><Label>Contact Email</Label><Input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={handleSubmit} disabled={saving} data-testid="save-vendor-btn">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Create Vendor</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== NEW ASSESSMENT DIALOG ====================
function NewAssessmentDialog({ open, onClose, onSuccess }) {
  const [vendors, setVendors] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [form, setForm] = useState({ vendor_id: '', questionnaire_id: '', title: '', description: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getVendors({}).then(setVendors).catch(() => {});
    getQuestionnaires({}).then(setQuestionnaires).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!form.vendor_id || !form.questionnaire_id || !form.title) { toast.error('Fill required fields'); return; }
    setSaving(true);
    try {
      const result = await createAssessment(form);
      toast.success('Assessment created');
      onSuccess(result.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Vendor Assessment</DialogTitle><DialogDescription>Select a vendor and questionnaire to begin</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Vendor *</Label>
            <Select value={form.vendor_id} onValueChange={v => setForm({ ...form, vendor_id: v })}>
              <SelectTrigger data-testid="select-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Questionnaire *</Label>
            <Select value={form.questionnaire_id} onValueChange={v => setForm({ ...form, questionnaire_id: v })}>
              <SelectTrigger data-testid="select-questionnaire"><SelectValue placeholder="Select questionnaire" /></SelectTrigger>
              <SelectContent>{questionnaires.map(q => <SelectItem key={q.id} value={q.id}>{q.name} ({q.total_questions} questions)</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Assessment Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Annual Security Assessment 2026" data-testid="assessment-title-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div><Label>Assigned To</Label><Input value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} placeholder="Email of assessor" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={handleSubmit} disabled={saving} data-testid="create-assessment-btn">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Create Assessment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CREATE CUSTOM QUESTIONNAIRE DIALOG ====================
function CreateQuestionnaireDialog({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', category: 'vendor', description: '', frameworks: '', uses_scoring: true, estimated_time: '30 minutes' });
  const [sections, setSections] = useState([{ id: 'section_1', name: 'General', description: '', questions: [] }]);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const addSection = () => {
    setSections([...sections, { id: `section_${sections.length + 1}`, name: '', description: '', questions: [] }]);
    setActiveSection(sections.length);
  };

  const removeSection = (sIdx) => {
    if (sections.length === 1) { toast.error('At least one section required'); return; }
    const updated = [...sections];
    updated.splice(sIdx, 1);
    setSections(updated);
    setActiveSection(Math.max(0, sIdx - 1));
  };

  const duplicateSection = (sIdx) => {
    const section = sections[sIdx];
    const newSection = {
      ...JSON.parse(JSON.stringify(section)),
      id: `section_${Date.now()}`,
      name: `${section.name} (Copy)`,
      questions: section.questions.map(q => ({ ...q, id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` }))
    };
    const updated = [...sections];
    updated.splice(sIdx + 1, 0, newSection);
    setSections(updated);
  };

  const addQuestion = (sectionIdx, type = 'yes_no') => {
    const updated = [...sections];
    const defaultQuestion = {
      id: `q_${Date.now()}`,
      text: '',
      type: type,
      required: true,
      weight: 'medium',
      scoring: true,
      score_yes: 10,
      score_no: 0,
      options: [],
      help_text: '',
      conditional_logic: null,
      validation: null
    };
    
    // Add type-specific defaults
    if (type === 'single_choice' || type === 'multiple_choice') {
      defaultQuestion.options = [
        { value: 'option_1', label: 'Option 1', score: 10 },
        { value: 'option_2', label: 'Option 2', score: 5 },
        { value: 'option_3', label: 'Option 3', score: 0 },
      ];
    } else if (type === 'rating_scale') {
      defaultQuestion.scale_min = 1;
      defaultQuestion.scale_max = 5;
      defaultQuestion.scale_labels = { 1: 'Poor', 3: 'Average', 5: 'Excellent' };
    } else if (type === 'file_upload') {
      defaultQuestion.allowed_types = ['pdf', 'doc', 'docx', 'xlsx', 'png', 'jpg'];
      defaultQuestion.max_file_size_mb = 10;
      defaultQuestion.scoring = false;
    }
    
    updated[sectionIdx].questions.push(defaultQuestion);
    setSections(updated);
  };

  const duplicateQuestion = (sIdx, qIdx) => {
    const updated = [...sections];
    const question = updated[sIdx].questions[qIdx];
    const newQuestion = {
      ...JSON.parse(JSON.stringify(question)),
      id: `q_${Date.now()}`,
      text: `${question.text} (Copy)`
    };
    updated[sIdx].questions.splice(qIdx + 1, 0, newQuestion);
    setSections(updated);
  };

  const moveQuestion = (sIdx, qIdx, direction) => {
    const updated = [...sections];
    const questions = updated[sIdx].questions;
    if (direction === 'up' && qIdx > 0) {
      [questions[qIdx], questions[qIdx - 1]] = [questions[qIdx - 1], questions[qIdx]];
    } else if (direction === 'down' && qIdx < questions.length - 1) {
      [questions[qIdx], questions[qIdx + 1]] = [questions[qIdx + 1], questions[qIdx]];
    }
    setSections(updated);
  };

  const updateQuestion = (sIdx, qIdx, field, value) => {
    const updated = [...sections];
    updated[sIdx].questions[qIdx][field] = value;
    // Add default options for choice types
    if (field === 'type' && (value === 'single_choice' || value === 'multiple_choice') && updated[sIdx].questions[qIdx].options.length === 0) {
      updated[sIdx].questions[qIdx].options = [
        { value: 'option_1', label: 'Option 1', score: 10 },
        { value: 'option_2', label: 'Option 2', score: 5 },
        { value: 'option_3', label: 'Option 3', score: 0 },
      ];
    }
    if (field === 'type' && value === 'rating_scale') {
      updated[sIdx].questions[qIdx].scale_min = updated[sIdx].questions[qIdx].scale_min || 1;
      updated[sIdx].questions[qIdx].scale_max = updated[sIdx].questions[qIdx].scale_max || 5;
    }
    setSections(updated);
  };

  const removeQuestion = (sIdx, qIdx) => {
    const updated = [...sections];
    updated[sIdx].questions.splice(qIdx, 1);
    setSections(updated);
  };

  const updateOption = (sIdx, qIdx, oIdx, field, value) => {
    const updated = [...sections];
    updated[sIdx].questions[qIdx].options[oIdx][field] = field === 'score' ? Number(value) : value;
    setSections(updated);
  };

  const addOption = (sIdx, qIdx) => {
    const updated = [...sections];
    const opts = updated[sIdx].questions[qIdx].options;
    opts.push({ value: `option_${opts.length + 1}`, label: '', score: 0 });
    setSections(updated);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    const totalQ = sections.reduce((sum, s) => sum + s.questions.length, 0);
    if (totalQ === 0) { toast.error('Add at least one question'); return; }
    setSaving(true);
    try {
      await createCustomQuestionnaire({
        ...form,
        frameworks: form.frameworks.split(',').map(f => f.trim()).filter(Boolean),
        sections
      });
      toast.success('Questionnaire created');
      onSuccess();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const questionTypes = [
    { value: 'yes_no', label: 'Yes/No', icon: '✓' },
    { value: 'yes_no_na', label: 'Yes/No/N/A', icon: '◐' },
    { value: 'single_choice', label: 'Single Choice', icon: '◉' },
    { value: 'multiple_choice', label: 'Multiple Choice', icon: '☑' },
    { value: 'text', label: 'Text', icon: '📝' },
    { value: 'number', label: 'Number', icon: '#' },
    { value: 'rating_scale', label: 'Rating Scale', icon: '⭐' },
    { value: 'file_upload', label: 'File Upload', icon: '📎' },
    { value: 'date', label: 'Date', icon: '📅' },
  ];

  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const totalScore = sections.reduce((sum, s) => s.questions.reduce((qs, q) => qs + (q.score_yes || q.options?.reduce((os, o) => Math.max(os, o.score || 0), 0) || 0), sum), 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Custom Questionnaire</DialogTitle>
          <DialogDescription>Build your own vendor assessment questionnaire with scoring and conditional logic</DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Form Header */}
          <div className="space-y-4 pb-4 border-b">
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. AI Vendor Security Assessment" data-testid="q-name-input" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="privacy">Privacy</SelectItem>
                    <SelectItem value="risk">Risk</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                    <SelectItem value="saas">SaaS</SelectItem>
                    <SelectItem value="ai">AI & Digital Products</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Estimated Time</Label><Input value={form.estimated_time} onChange={e => setForm({ ...form, estimated_time: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
              <div><Label>Frameworks (comma-separated)</Label><Input value={form.frameworks} onChange={e => setForm({ ...form, frameworks: e.target.value })} placeholder="ISO 27001, SOC 2, GDPR" /></div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span><strong>{sections.length}</strong> sections</span>
              <span><strong>{totalQuestions}</strong> questions</span>
              <span>Max score: <strong>{totalScore}</strong> points</span>
            </div>
          </div>

          {/* Sections and Questions */}
          <div className="flex-1 overflow-hidden flex gap-4 pt-4">
            {/* Section Navigator */}
            <div className="w-48 space-y-2 overflow-y-auto">
              <Label className="text-xs text-gray-400 uppercase tracking-wide">Sections</Label>
              {sections.map((section, sIdx) => (
                <div 
                  key={section.id}
                  className={`p-2 rounded-lg cursor-pointer border transition-all ${activeSection === sIdx ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50 border-transparent'}`}
                  onClick={() => setActiveSection(sIdx)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium truncate">{section.name || `Section ${sIdx + 1}`}</span>
                    <Badge variant="secondary" className="text-xs">{section.questions.length}</Badge>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" className="w-full" onClick={addSection}><Plus className="w-3 h-3 mr-1" />Add Section</Button>
            </div>

            {/* Active Section Editor */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {sections[activeSection] && (
                <>
                  <div className="flex items-center gap-3 sticky top-0 bg-white pb-2 border-b">
                    <Input 
                      className="text-lg font-semibold flex-1" 
                      placeholder="Section name" 
                      value={sections[activeSection].name} 
                      onChange={e => { const u = [...sections]; u[activeSection].name = e.target.value; setSections(u); }} 
                    />
                    <Button variant="ghost" size="sm" onClick={() => duplicateSection(activeSection)} title="Duplicate Section"><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => removeSection(activeSection)} title="Delete Section"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                  <Input 
                    placeholder="Section description (optional)" 
                    value={sections[activeSection].description} 
                    onChange={e => { const u = [...sections]; u[activeSection].description = e.target.value; setSections(u); }} 
                  />

                  {/* Questions */}
                  {sections[activeSection].questions.map((q, qIdx) => (
                    <div key={q.id} className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-100">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs font-mono text-gray-400 bg-gray-200 px-2 py-0.5 rounded">Q{qIdx + 1}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(activeSection, qIdx, 'up')} disabled={qIdx === 0}><ChevronUp className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(activeSection, qIdx, 'down')} disabled={qIdx === sections[activeSection].questions.length - 1}><ChevronDown className="w-3 h-3" /></Button>
                        </div>
                        <div className="flex-1 space-y-3">
                          <Input 
                            className="font-medium" 
                            placeholder="Question text" 
                            value={q.text} 
                            onChange={e => updateQuestion(activeSection, qIdx, 'text', e.target.value)} 
                          />
                          <Input 
                            className="text-sm text-gray-500" 
                            placeholder="Help text / guidance (optional)" 
                            value={q.help_text || ''} 
                            onChange={e => updateQuestion(activeSection, qIdx, 'help_text', e.target.value)} 
                          />
                          <div className="flex flex-wrap gap-2 items-center">
                            <Select value={q.type} onValueChange={v => updateQuestion(activeSection, qIdx, 'type', v)}>
                              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {questionTypes.map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={q.weight} onValueChange={v => updateQuestion(activeSection, qIdx, 'weight', v)}>
                              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="critical">Critical</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="none">None</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2">
                              <Checkbox checked={q.required} onCheckedChange={v => updateQuestion(activeSection, qIdx, 'required', v)} id={`req-${q.id}`} />
                              <Label htmlFor={`req-${q.id}`} className="text-xs">Required</Label>
                            </div>
                            {(q.type === 'yes_no' || q.type === 'yes_no_na') && (
                              <>
                                <div className="flex items-center gap-1 ml-2"><Label className="text-xs">Yes:</Label><Input type="number" className="w-16 h-8" value={q.score_yes} onChange={e => updateQuestion(activeSection, qIdx, 'score_yes', Number(e.target.value))} /></div>
                                <div className="flex items-center gap-1"><Label className="text-xs">No:</Label><Input type="number" className="w-16 h-8" value={q.score_no} onChange={e => updateQuestion(activeSection, qIdx, 'score_no', Number(e.target.value))} /></div>
                              </>
                            )}
                          </div>
                          
                          {/* Rating Scale Config */}
                          {q.type === 'rating_scale' && (
                            <div className="flex items-center gap-3 p-2 bg-white rounded border">
                              <Label className="text-xs">Scale:</Label>
                              <Input type="number" className="w-16 h-8" value={q.scale_min || 1} onChange={e => updateQuestion(activeSection, qIdx, 'scale_min', Number(e.target.value))} />
                              <span>to</span>
                              <Input type="number" className="w-16 h-8" value={q.scale_max || 5} onChange={e => updateQuestion(activeSection, qIdx, 'scale_max', Number(e.target.value))} />
                              <span className="text-xs text-gray-400">(Score = selected value × 2)</span>
                            </div>
                          )}
                          
                          {/* File Upload Config */}
                          {q.type === 'file_upload' && (
                            <div className="flex items-center gap-3 p-2 bg-white rounded border">
                              <Label className="text-xs">Max size:</Label>
                              <Input type="number" className="w-20 h-8" value={q.max_file_size_mb || 10} onChange={e => updateQuestion(activeSection, qIdx, 'max_file_size_mb', Number(e.target.value))} />
                              <span className="text-xs">MB</span>
                              <Label className="text-xs ml-2">Allowed:</Label>
                              <Input className="flex-1 h-8" placeholder="pdf, doc, xlsx" value={(q.allowed_types || []).join(', ')} onChange={e => updateQuestion(activeSection, qIdx, 'allowed_types', e.target.value.split(',').map(t => t.trim()))} />
                            </div>
                          )}
                          
                          {/* Choice Options */}
                          {(q.type === 'single_choice' || q.type === 'multiple_choice') && (
                            <div className="space-y-2 p-2 bg-white rounded border">
                              {(q.options || []).map((o, oIdx) => (
                                <div key={oIdx} className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 w-6">{oIdx + 1}.</span>
                                  <Input className="flex-1 h-8" placeholder="Option label" value={o.label} onChange={e => updateOption(activeSection, qIdx, oIdx, 'label', e.target.value)} />
                                  <Input className="w-20 h-8" type="number" placeholder="Score" value={o.score} onChange={e => updateOption(activeSection, qIdx, oIdx, 'score', e.target.value)} />
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { const u = [...sections]; u[activeSection].questions[qIdx].options.splice(oIdx, 1); setSections(u); }}><X className="w-3 h-3" /></Button>
                                </div>
                              ))}
                              <Button variant="ghost" size="sm" onClick={() => addOption(activeSection, qIdx)}><Plus className="w-3 h-3 mr-1" />Add Option</Button>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicateQuestion(activeSection, qIdx)} title="Duplicate"><Copy className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeQuestion(activeSection, qIdx)} title="Delete"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add Question Buttons */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <span className="text-xs text-gray-400 self-center mr-2">Add:</span>
                    {questionTypes.slice(0, 6).map(t => (
                      <Button key={t.value} variant="outline" size="sm" onClick={() => addQuestion(activeSection, t.value)}>
                        {t.icon} {t.label}
                      </Button>
                    ))}
                    <Select onValueChange={v => addQuestion(activeSection, v)}>
                      <SelectTrigger className="w-32 h-8"><SelectValue placeholder="More..." /></SelectTrigger>
                      <SelectContent>
                        {questionTypes.slice(6).map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-questionnaire-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Create Questionnaire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== VENDOR DETAIL VIEW ====================
function VendorDetailView({ vendorId, navigate }) {
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { getVendor } = await import('../../services/vendorAssessment');
        setVendor(await getVendor(vendorId));
      } catch { toast.error('Vendor not found'); navigate('/grc/vendor-assessment'); }
      setLoading(false);
    })();
  }, [vendorId]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!vendor) return null;

  return (
    <div className="space-y-6" data-testid="vendor-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/grc/vendor-assessment?tab=vendors')}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{vendor.name}</h1>
          <p className="text-sm text-gray-500">{vendor.industry || 'N/A'}</p>
        </div>
        <Badge className={riskColors[vendor.overall_risk_level || 'not_assessed']}>{getRiskLevelLabel(vendor.overall_risk_level || 'not_assessed')}</Badge>
        {vendor.overall_risk_score != null && <span className="text-xl font-bold">{Math.round(vendor.overall_risk_score)}%</span>}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><Label className="text-xs text-gray-400">Risk Tier</Label><p className="font-semibold capitalize">{vendor.risk_tier || 'medium'}</p></CardContent></Card>
        <Card><CardContent className="p-4"><Label className="text-xs text-gray-400">Last Assessment</Label><p className="font-semibold">{vendor.last_assessment_date ? formatDate(vendor.last_assessment_date) : 'Never'}</p></CardContent></Card>
        <Card><CardContent className="p-4"><Label className="text-xs text-gray-400">Next Due</Label><p className="font-semibold">{vendor.next_assessment_due ? formatDate(vendor.next_assessment_due) : 'N/A'}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Assessment History</CardTitle></CardHeader>
        <CardContent>
          {(vendor.assessments || []).length === 0 ? (
            <p className="text-sm text-gray-400">No assessments yet for this vendor</p>
          ) : (
            <div className="space-y-2">
              {vendor.assessments.map(a => (
                <div key={a.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/grc/vendor-assessment/assessments/${a.id}`)}>
                  <div>
                    <p className="font-medium text-sm">{a.title}</p>
                    <p className="text-xs text-gray-400">{a.questionnaire_name} &middot; {formatDate(a.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={statusColors[a.status]}>{getStatusLabel(a.status)}</Badge>
                    {a.risk_score != null && <Badge className={riskColors[a.risk_level || 'not_assessed']}>{Math.round(a.risk_score)}%</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== ASSESSMENT FILL VIEW ====================
function AssessmentFillView({ assessmentId, navigate }) {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [jiraLoading, setJiraLoading] = useState(false);

  useEffect(() => { loadAssessment(); }, [assessmentId]);

  const loadAssessment = async () => {
    setLoading(true);
    try {
      const data = await getAssessment(assessmentId);
      setAssessment(data);
      // Populate existing responses
      const existing = {};
      (data.responses || []).forEach(sr => {
        (sr.responses || []).forEach(r => { existing[r.question_id] = r.value; });
      });
      setResponses(existing);
      if (data.questionnaire?.sections?.[0]) setActiveSection(data.questionnaire.sections[0].id);
    } catch { toast.error('Assessment not found'); navigate('/grc/vendor-assessment?tab=assessments'); }
    setLoading(false);
  };

  const handleSaveSection = async (sectionId) => {
    const section = assessment.questionnaire?.sections?.find(s => s.id === sectionId);
    if (!section) return;
    setSaving(true);
    try {
      const sectionResponses = section.questions.map(q => ({
        question_id: q.id,
        value: responses[q.id] ?? ''
      })).filter(r => r.value !== '' && r.value !== undefined);

      const updated = await submitAssessmentResponses(assessmentId, sectionId, sectionResponses);
      setAssessment(prev => ({ ...prev, ...updated, questionnaire: prev.questionnaire }));
      toast.success('Section saved');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleComplete = async () => {
    // Validate all required questions are answered
    const allQuestions = assessment.questionnaire?.sections?.flatMap(s => s.questions) || [];
    const requiredQuestions = allQuestions.filter(q => q.required);
    const unansweredRequired = requiredQuestions.filter(q => {
      const answer = responses[q.id];
      return answer === undefined || answer === null || answer === '' || (Array.isArray(answer) && answer.length === 0);
    });

    if (unansweredRequired.length > 0) {
      toast.error(`Please answer all required questions. ${unansweredRequired.length} required question(s) unanswered.`);
      return;
    }

    if (!window.confirm('Mark this assessment as complete? Final scores will be calculated.')) return;
    setSaving(true);
    try {
      const updated = await completeAssessment(assessmentId);
      setAssessment(prev => ({ ...prev, ...updated, questionnaire: prev.questionnaire }));
      toast.success('Assessment completed! Risk score calculated.');
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!assessment) return null;

  const q = assessment.questionnaire;
  const isCompleted = assessment.status === 'completed';

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/vendor-assessment/assessments/${assessmentId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendor_assessment_${assessmentId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch { toast.error('Failed to generate PDF'); }
    setPdfLoading(false);
  };

  const handleCreateJiraTickets = async () => {
    if (!window.confirm('Create Jira tickets for all findings? Ensure Jira is configured in Settings > Integrations.')) return;
    setJiraLoading(true);
    try {
      const token = localStorage.getItem('token');
      const findings = (assessment.findings || []).map(f => ({
        title: `[${(f.severity || 'info').toUpperCase()}] ${f.question || 'Vendor Assessment Finding'}`,
        description: `Vendor: ${assessment.vendor_name}\nAssessment: ${assessment.title}\nSection: ${f.section || 'N/A'}\nSeverity: ${(f.severity || 'info').toUpperCase()}\n\nFinding: ${f.question || ''}\nRecommendation: ${f.recommendation || 'N/A'}`,
        severity: f.severity || 'medium',
        labels: ['vendor-assessment', assessment.vendor_name?.replace(/\s+/g, '-').toLowerCase()].filter(Boolean)
      }));
      const response = await axios.post(
        `${API_URL}/api/integrations/jira/bulk-create-tickets`,
        { findings },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const created = response.data?.created_count || response.data?.length || 0;
      toast.success(`Created ${created} Jira ticket(s) from findings`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to create Jira tickets. Check Jira configuration in Settings.';
      toast.error(msg);
    }
    setJiraLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="assessment-fill">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/grc/vendor-assessment?tab=assessments')}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">{assessment.title}</h1>
          <p className="text-sm text-gray-500">{assessment.vendor_name} &middot; {assessment.questionnaire_name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={statusColors[assessment.status]}>{getStatusLabel(assessment.status)}</Badge>
          {assessment.risk_score != null && (
            <div className="flex items-center gap-2">
              <Badge className={riskColors[assessment.risk_level || 'not_assessed']}>{getRiskLevelLabel(assessment.risk_level || 'not_assessed')}</Badge>
              <span className="text-lg font-bold">{Math.round(assessment.risk_score)}%</span>
            </div>
          )}
          {isCompleted && (
            <>
              <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={pdfLoading} data-testid="download-pdf-btn">
                {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}PDF Report
              </Button>
              {assessment.findings?.length > 0 && (
                <Button variant="outline" size="sm" onClick={handleCreateJiraTickets} disabled={jiraLoading} data-testid="create-jira-btn">
                  {jiraLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <TicketCheck className="w-4 h-4 mr-1" />}Create Jira Tickets
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm text-gray-500">{Math.round(assessment.completion_percentage || 0)}% complete</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3">
            <div className="bg-blue-500 h-3 rounded-full transition-all" style={{ width: `${assessment.completion_percentage || 0}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Completed: show findings and recommendations */}
      {isCompleted && assessment.findings && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Findings</CardTitle></CardHeader>
          <CardContent>
            {assessment.findings.length === 0 ? <p className="text-sm text-gray-500">No findings</p> : (
              <div className="space-y-2">
                {assessment.findings.map((f, i) => (
                  <div key={i} className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={f.severity === 'high' ? 'bg-red-100 text-red-700' : f.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>{f.severity}</Badge>
                      <span className="text-xs text-gray-400">{f.section}</span>
                    </div>
                    <p className="text-sm">{f.question}</p>
                    <p className="text-xs text-gray-500 mt-1">{f.recommendation}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isCompleted && assessment.recommendations && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-5 h-5 text-blue-500" />Recommendations</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {assessment.recommendations.map((r, i) => <li key={i} className="text-sm flex items-start gap-2"><ChevronRight className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />{r}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Section tabs */}
      {q?.sections && (
        <div className="flex gap-6">
          <div className="w-64 shrink-0 space-y-1">
            {q.sections.map((s, i) => {
              const sectionResp = (assessment.responses || []).find(r => r.section_id === s.id);
              const isDone = sectionResp?.completed;
              
              // Check if section has any answers (partially complete)
              const sectionQuestions = s.questions || [];
              const answeredQuestions = sectionQuestions.filter(q => {
                const answer = responses[q.id];
                return answer !== undefined && answer !== null && answer !== '' && !(Array.isArray(answer) && answer.length === 0);
              });
              const hasPartialAnswers = answeredQuestions.length > 0 && answeredQuestions.length < sectionQuestions.length;
              
              // Check if all required questions are answered
              const requiredQuestions = sectionQuestions.filter(q => q.required);
              const answeredRequired = requiredQuestions.filter(q => {
                const answer = responses[q.id];
                return answer !== undefined && answer !== null && answer !== '' && !(Array.isArray(answer) && answer.length === 0);
              });
              const isIncomplete = requiredQuestions.length > 0 && answeredRequired.length < requiredQuestions.length;
              
              return (
                <button key={s.id} onClick={() => setActiveSection(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === s.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50 text-gray-600'}`}
                  data-testid={`section-btn-${s.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{i + 1}. {s.name}</span>
                    {isDone && !isIncomplete && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                    {isDone && isIncomplete && <AlertCircle className="w-4 h-4 text-amber-500" />}
                    {!isDone && hasPartialAnswers && <Clock className="w-4 h-4 text-amber-400" />}
                  </div>
                  {isDone && isIncomplete && (
                    <p className="text-xs text-amber-600 mt-1">
                      {answeredRequired.length}/{requiredQuestions.length} required
                    </p>
                  )}
                </button>
              );
            })}
            {!isCompleted && (
              <Button className="w-full mt-4" onClick={handleComplete} disabled={saving} data-testid="complete-assessment-btn">
                <CheckCircle className="w-4 h-4 mr-2" />Complete Assessment
              </Button>
            )}
          </div>

          <div className="flex-1">
            {q.sections.filter(s => s.id === activeSection).map(section => (
              <Card key={section.id}>
                <CardHeader>
                  <CardTitle className="text-base">{section.name}</CardTitle>
                  {section.description && <CardDescription>{section.description}</CardDescription>}
                </CardHeader>
                <CardContent className="space-y-6">
                  {section.questions.map((question, qi) => (
                    <div key={question.id} className="space-y-2" data-testid={`question-${question.id}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-mono text-gray-400 mt-1 shrink-0">Q{qi + 1}</span>
                        <div className="flex-1">
                          <Label className="text-sm font-medium">{question.text}{question.required && <span className="text-red-500 ml-1">*</span>}</Label>
                          {question.help_text && <p className="text-xs text-blue-500 flex items-center gap-1 mt-0.5"><Info className="w-3 h-3" />{question.help_text}</p>}
                        </div>
                      </div>

                      {(question.type === 'yes_no' || question.type === 'yes_no_na') && (
                        <div className="flex gap-2 pl-6">
                          {['yes', 'no', ...(question.type === 'yes_no_na' ? ['n/a'] : [])].map(val => (
                            <button key={val} onClick={() => !isCompleted && setResponses({ ...responses, [question.id]: val })}
                              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${responses[question.id] === val ? (val === 'yes' ? 'bg-emerald-100 border-emerald-300 text-emerald-700' : val === 'no' ? 'bg-red-100 border-red-300 text-red-700' : 'bg-gray-100 border-gray-300 text-gray-700') : 'border-gray-200 hover:border-gray-300'} ${isCompleted ? 'cursor-default' : 'cursor-pointer'}`}
                              data-testid={`answer-${question.id}-${val}`}
                            >
                              {val === 'yes' ? 'Yes' : val === 'no' ? 'No' : 'N/A'}
                            </button>
                          ))}
                        </div>
                      )}

                      {question.type === 'single_choice' && (
                        <div className="space-y-2 pl-6">
                          {(question.options || []).map(opt => (
                            <button key={opt.value} onClick={() => !isCompleted && setResponses({ ...responses, [question.id]: opt.value })}
                              className={`w-full text-left px-4 py-2 rounded-lg border text-sm transition-colors ${responses[question.id] === opt.value ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 hover:border-gray-300'} ${isCompleted ? 'cursor-default' : 'cursor-pointer'}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-4 h-4 rounded-full border-2 ${responses[question.id] === opt.value ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                  {responses[question.id] === opt.value && <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />}
                                </div>
                                {opt.label}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {question.type === 'text' && (
                        <div className="pl-6"><Textarea value={responses[question.id] || ''} onChange={e => setResponses({ ...responses, [question.id]: e.target.value })} disabled={isCompleted} placeholder="Enter your response..." /></div>
                      )}

                      {question.type === 'number' && (
                        <div className="pl-6"><Input type="number" value={responses[question.id] || ''} onChange={e => setResponses({ ...responses, [question.id]: e.target.value })} disabled={isCompleted} /></div>
                      )}

                      {question.type === 'scale' && (
                        <div className="pl-6 space-y-2">
                          <div className="flex gap-1">
                            {Array.from({ length: (question.scale?.max || 5) - (question.scale?.min || 1) + 1 }, (_, i) => i + (question.scale?.min || 1)).map(val => (
                              <button
                                key={val}
                                onClick={() => !isCompleted && setResponses({ ...responses, [question.id]: val })}
                                className={`w-12 h-12 rounded-lg border-2 text-sm font-bold transition-colors ${responses[question.id] === val ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-200 hover:border-blue-300 text-gray-700'} ${isCompleted ? 'cursor-default' : 'cursor-pointer'}`}
                                data-testid={`scale-${question.id}-${val}`}
                              >
                                {val}
                              </button>
                            ))}
                          </div>
                          {question.scale?.labels && (
                            <div className="flex justify-between text-xs text-gray-500 px-1">
                              <span>{question.scale.labels[0] || ''}</span>
                              <span>{question.scale.labels[question.scale.labels.length - 1] || ''}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {question.type === 'multiple_choice' && (
                        <div className="space-y-2 pl-6">
                          {(question.options || []).map(opt => {
                            const selectedValues = Array.isArray(responses[question.id]) ? responses[question.id] : [];
                            const isSelected = selectedValues.includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                onClick={() => {
                                  if (isCompleted) return;
                                  const newValues = isSelected
                                    ? selectedValues.filter(v => v !== opt.value)
                                    : [...selectedValues, opt.value];
                                  setResponses({ ...responses, [question.id]: newValues });
                                }}
                                className={`w-full text-left px-4 py-2 rounded-lg border text-sm transition-colors ${isSelected ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 hover:border-gray-300'} ${isCompleted ? 'cursor-default' : 'cursor-pointer'}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  {opt.label}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {question.type === 'date' && (
                        <div className="pl-6">
                          <Input type="date" value={responses[question.id] || ''} onChange={e => setResponses({ ...responses, [question.id]: e.target.value })} disabled={isCompleted} />
                        </div>
                      )}
                    </div>
                  ))}

                  {!isCompleted && (
                    <div className="flex justify-end pt-4 border-t">
                      <Button onClick={() => handleSaveSection(section.id)} disabled={saving} data-testid={`save-section-${section.id}`}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Section
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
